import React from "react"
import { graphql } from "gatsby"
import { GatsbyImage } from "gatsby-plugin-image"
import styled from "styled-components"
import { hideVisually, rem } from "polished"
import { Meta } from "@outdoormap/gatsby-plugin-brand-sites"

import Layout from "../components/layout"
import Map from "../components/map"
import MediaBlock from "../components/media-block"
import TypeCtas from "../components/type-ctas"
import IndexSites from "../components/index-sites"
import PostsHighlights from "../components/posts-highlights"
import EventsUpcoming from "../components/events-upcoming"
import { ReactComponent as SvgBulb } from "../assets/images/icons/bulb.svg"
import { ReactComponent as SvgCalendar } from "../assets/images/icons/calendar.svg"

const MapWrap = styled.section`
  height: 80vh;
  min-height: ${rem(220)};
  margin-top: ${rem(60)};
  position: relative;

  @media ${({ theme }) => theme.mq.largeDown} {
    margin-top: ${rem(40)};
  }

  > h2 {
    ${hideVisually()}
  }

  > .gatsby-image-wrapper {
    width: ${rem(200)};
    height: ${rem(200)};
    position: absolute !important;
    z-index: 1;
    top: ${rem(-30)};
    right: ${rem(100)};
    pointer-events: none;
    filter: drop-shadow(0 ${rem(10)} ${rem(15)} rgba(0, 0, 0, 0.7));

    @media ${({ theme }) => theme.mq.largeDown} {
      width: ${rem(140)};
      height: ${rem(140)};
      right: ${rem(80)};
      filter: drop-shadow(0 ${rem(5)} ${rem(10)} rgba(0, 0, 0, 0.5));
    }

    @media ${({ theme }) => theme.mq.mediumDown} {
      display: none;
    }
  }
`

const Hr = styled.div`
  ${({ theme }) => theme.grid.container()}

  div {
    height: 0;
    margin-bottom: ${rem(40)};
    display: block;
    overflow: hidden;
    border-top: 1px solid ${({ theme }) => theme.colors.timberWolf};
  }
`

const IndexPage = ({
  data: {
    pageAbout,
    config,
    translations,
    latestArticles,
    latestEvents,
    packages,
    stays,
    eatDrink,
    hiking,
    biking,
    fishing,
    riding,
    canoeing,
    more,
    imgCycling,
    imgMapPreview,
  },
}) => {
  const sites = []

  const findType = (name) => config.types.find((t) => t.name === name)

  if (packages.nodes.length) {
    sites.push({ type: findType(`packages`), items: packages.nodes })
  }

  if (stays.nodes.length) {
    sites.push({ type: findType(`stays`), items: stays.nodes })
  }

  if (eatDrink.nodes.length) {
    sites.push({ type: findType(`eatDrink`), items: eatDrink.nodes })
  }

  if (hiking.nodes.length) {
    sites.push({ type: findType(`hiking`), items: hiking.nodes })
  }

  if (biking.nodes.length) {
    sites.push({ type: findType(`biking`), items: biking.nodes })
  }

  if (riding.nodes.length) {
    sites.push({ type: findType(`riding`), items: riding.nodes })
  }

  if (fishing.nodes.length) {
    sites.push({ type: findType(`fishing`), items: fishing.nodes })
  }

  if (canoeing.nodes.length) {
    sites.push({ type: findType(`canoeing`), items: canoeing.nodes })
  }

  if (more.nodes.length) {
    sites.push({ type: findType(`more`), items: more.nodes })
  }

  const mapQuery = [
    `guide=${process.env.GATSBY_NATURKARTAN_GUIDE_ID}`,
    // ...[33, 116, 37, 34, 31, 16, 55, 56, 117].map((c) => `category=${c}`), // hiking,pilgrim-trail,canoeing,riding,biking,mountainbiking,hostel,camping,package
  ].join(`&`)

  return (
    <Layout>
      <MediaBlock
        headingLevel={1}
        descriptionHeadingLevel={2}
        data={{
          // video: `/tmp/video.mp4`,
          image: imgCycling,
          title: config.pages.index.hero.title,
          description: config.pages.index.hero.description,
          cta: {
            title: config.pages.index.hero.cta,
            url: pageAbout && pageAbout.path,
          },
        }}
      />

      <TypeCtas />

      <MapWrap>
        <h2 dangerouslySetInnerHTML={{ __html: translations.global.map }} />

        <GatsbyImage
          image={imgMapPreview.childImageSharp.gatsbyImageData}
          objectFit="contain"
          alt={translations.global.map}
        />

        <Map
          query={mapQuery}
          menu="fullscreen"
          scrollZoom="false"
          styleControl="fullscreen"
          zoomControl="true"
          measureControl="fullscreen"
          locationControl="fullscreen"
          printControl="fullscreen"
          legendControl="true"
          legend="true"
        />
      </MapWrap>

      <PostsHighlights
        data={latestArticles.nodes}
        heading={translations.global.latestArticles}
        moreText={translations.global.seeAll}
        moreLink={`/${config.pages.articles.slug}`}
        icon={<SvgBulb />}
      />

      {!!latestEvents.nodes.length && (
        <EventsUpcoming
          data={latestEvents.nodes}
          heading={translations.pages.events.title}
          moreText={translations.global.seeAll}
          moreLink={`/${config.pages.events.slug}`}
          icon={<SvgCalendar />}
        />
      )}

      {sites.map(({ type, items }, i) => (
        <React.Fragment key={i}>
          {i > 0 && (
            <Hr>
              <div />
            </Hr>
          )}

          <IndexSites type={type} sites={items} />
        </React.Fragment>
      ))}
    </Layout>
  )
}

export default IndexPage

export const Head = ({ data: { config } }) => {
  return (
    <Meta
      data={{
        title: config.pages.index.title,
        titleOverridePattern: true,
      }}
    />
  )
}

export const pageQuery = graphql`
  query {
    translations: translationsJson {
      global {
        map
        seeAll
        latestArticles
      }
    }

    config: configJson {
      pages {
        index {
          title
          hero {
            title
            description
            cta
          }
        }
        articles {
          slug
        }
        events {
          slug
          title
        }
      }

      types {
        name
        slug
        color
        title
        tagline
        description: description_short
      }
    }

    pageAbout: pagesJson(nk_id: { eq: "11476" }) {
      path
    }

    packages: allSitesJson(
      filter: { the_type: { eq: "packages" } }
      sort: { name: ASC }
      limit: 8
    ) {
      nodes {
        ...SitesCardFragment
      }
    }

    stays: allSitesJson(
      filter: { the_type: { eq: "stays" } }
      sort: { name: ASC }
      limit: 8
    ) {
      nodes {
        ...SitesCardFragment
      }
    }

    eatDrink: allSitesJson(
      filter: { the_type: { eq: "eatDrink" } }
      sort: { name: ASC }
      limit: 8
    ) {
      nodes {
        ...SitesCardFragment
      }
    }

    hiking: allSitesJson(
      filter: { the_type: { eq: "hiking" } }
      sort: { name: ASC }
      limit: 8
    ) {
      nodes {
        ...SitesCardFragment
      }
    }

    biking: allSitesJson(
      filter: { the_type: { eq: "biking" } }
      sort: { name: ASC }
      limit: 8
    ) {
      nodes {
        ...SitesCardFragment
      }
    }

    riding: allSitesJson(
      filter: { the_type: { eq: "riding" } }
      sort: { name: ASC }
      limit: 8
    ) {
      nodes {
        ...SitesCardFragment
      }
    }

    fishing: allSitesJson(
      filter: { the_type: { eq: "fishing" } }
      sort: { name: ASC }
      limit: 8
    ) {
      nodes {
        ...SitesCardFragment
      }
    }

    canoeing: allSitesJson(
      filter: { the_type: { eq: "canoeing" } }
      sort: { name: ASC }
      limit: 8
    ) {
      nodes {
        ...SitesCardFragment
      }
    }

    more: allSitesJson(
      filter: { the_type: { eq: "more" } }
      sort: { name: ASC }
      limit: 8
    ) {
      nodes {
        ...SitesCardFragment
      }
    }

    latestArticles: allArticlesJson(sort: { publish_at: DESC }, limit: 3) {
      nodes {
        ...ArticlesCardFragment
      }
    }

    latestEvents: allEventsJson(sort: { occurs_at: ASC }, limit: 3) {
      nodes {
        ...EventsCardFragment
      }
    }

    imgCycling: file(
      sourceInstanceName: { eq: "images" }
      relativePath: { eq: "content/tiveden-dimma.jpg" }
    ) {
      childImageSharp {
        gatsbyImageData(width: 1280)
      }
    }

    imgMapPreview: file(
      sourceInstanceName: { eq: "images" }
      relativePath: { eq: "content/map-preview.png" }
    ) {
      childImageSharp {
        gatsbyImageData(width: 768, placeholder: NONE)
      }
    }
  }
`
